import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ReEstimateDialog } from "./ReEstimateDialog"
import { Children } from "../../../../components/miscellianous/children"
import { ProjectContext } from "../../../../core/context/project/ProjectContext"
import { ProjectCalculationStatus } from "../../../../core/dto/project/ProjectCalculationStatus"
import { clearTimer } from "../../../../core/services/helper-service"

const TIME_STEP_MS = 3000

export const ReEstimatingDialogContext = React.createContext<ReEstimatingDialogStore>({} as ReEstimatingDialogStore)

export function ReEstimatingDialogContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { project, refreshProject } = useContext(ProjectContext)

  const [isReEstimateDialogOpen, setIsReEstimateDialogOpen] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)

  const onClose: () => void = useCallback(() => {
    // Display an almost finished bar before actually closing the pop up
    setProgress(99)

    setTimeout(() => {
      setIsReEstimateDialogOpen(false)
      setProgress(0)
      clearTimer(intervalIdRef)
    }, 500)
  }, [])

  const handleOpen: () => void = useCallback(() => {
    setIsReEstimateDialogOpen(true)
  }, [])

  useEffect(() => {
    if (project?.calculationStatus === ProjectCalculationStatus.IN_PROGRESS) {
      handleOpen()
      // clearTimer(intervalIdRef)
      // Long pooling to check project calculation status
      intervalIdRef.current = setTimeout(() => {
        // We just need to refresh the project.
        // The useEffect above update the dialog open state
        refreshProject()
      }, TIME_STEP_MS)
    } else {
      onClose()
    }

    return () => {
      clearTimer(intervalIdRef)
    }
  }, [isReEstimateDialogOpen, refreshProject, project, handleOpen, onClose])

  const reEstimatingDialogStore: ReEstimatingDialogStore = useMemo(
    () => ({
      isReEstimateDialogOpen,
      handleOpen,
    }),
    [handleOpen, isReEstimateDialogOpen]
  )
  return (
    <ReEstimatingDialogContext.Provider value={reEstimatingDialogStore}>
      <ReEstimateDialog isOpen={isReEstimateDialogOpen} onClose={onClose} progress={progress} setProgress={setProgress} />
      {children}
    </ReEstimatingDialogContext.Provider>
  )
}

export interface ReEstimatingDialogStore {
  isReEstimateDialogOpen: boolean
  handleOpen(): void
}
